import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { getApiBaseUrl } from '../apiBase';
import ConsentAcceptPanel from './ConsentAcceptPanel';
import { useTranslation } from 'react-i18next';

// Те же исключения, что у EmailVerifyGate — на этих страницах либо ещё не залогинены,
// либо это отдельный сценарий (киоск СКУД), которому наши документы не нужны.
const EXCLUDED_PREFIXES = ['/login', '/register', '/select-profile', '/skud/kiosk', '/demo-login', '/activate', '/invite'];
const DISMISSED_NOTICE_KEY = 'consentNoticeDismissedVersion';

/**
 * Два независимых уровня (см. ConsentStatusEvaluator на бэке):
 * — noticeAvailable: мягкий баннер сверху, можно закрыть — просто "документы обновились",
 *   никогда не мешает работать. Закрывается на конкретную версию (localStorage), при выходе
 *   следующей версии появится снова.
 * — blocked: реальная блокировка (только если версия помечена RequiresReconsent и прошёл
 *   переходный период) — блокирующее окно без кнопки "Не сейчас", синхронизировано с
 *   ConsentCheckMiddleware на бэке (403 consent_outdated на любой запрос).
 */
export default function ConsentGate({ children }) {
    const { t } = useTranslation(['consent', 'common']);
    const location = useLocation();
    const [status, setStatus] = useState(null);
    const [checked, setChecked] = useState(false);
    const [noticeDismissed, setNoticeDismissed] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('token');
        const excluded = EXCLUDED_PREFIXES.some(p => location.pathname.startsWith(p));
        if (!token || excluded) {
            setChecked(true);
            setStatus(null);
            return;
        }

        let cancelled = false;
        axios.get(`${getApiBaseUrl()}/consent/my-status`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(res => {
                if (cancelled) return;
                setStatus(res.data);
                const dismissedVersion = localStorage.getItem(DISMISSED_NOTICE_KEY);
                setNoticeDismissed(!!dismissedVersion && dismissedVersion === res.data?.consentNoticeVersion);
            })
            .catch(() => {
                if (cancelled) return;
                setStatus(null);
            })
            .finally(() => {
                if (!cancelled) setChecked(true);
            });
        return () => { cancelled = true; };
    }, [location.pathname]);

    function handleAccepted() {
        setStatus(s => s ? { ...s, consentOutdated: false, consentNoticeAvailable: false } : s);
    }

    function dismissNotice() {
        if (status?.consentNoticeVersion) {
            localStorage.setItem(DISMISSED_NOTICE_KEY, status.consentNoticeVersion);
        }
        setNoticeDismissed(true);
    }

    if (!checked) return <>{children}</>;

    const blocked = !!status?.consentOutdated;
    const showNotice = !blocked && status?.consentNoticeAvailable && !noticeDismissed;

    return (
        <>
            {showNotice && (
                <div style={{
                    position: 'sticky', top: 0, zIndex: 2500,
                    background: '#fff7ed', borderBottom: '1px solid #fdba74',
                    padding: '10px 16px', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', gap: 12, flexWrap: 'wrap', fontSize: 13, color: '#7c2d12'
                }}>
                    <span>
                        📄 {t('consent:noticeText')}
                        {status?.consentNoticeChangeSummary ? ` — ${status.consentNoticeChangeSummary}` : ''}
                    </span>
                    <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: '#c2410c', fontWeight: 700 }}>
                        {t('consent:noticeReview')}
                    </a>
                    <button
                        type="button"
                        onClick={dismissNotice}
                        style={{ border: 'none', background: 'transparent', color: '#9a3412', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 4 }}
                        aria-label={t('common:close', { defaultValue: 'Закрыть' })}
                    >
                        ✕
                    </button>
                </div>
            )}

            {children}

            {blocked && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 3000, padding: 16, overflowY: 'auto'
                }}>
                    <div style={{ width: '100%', maxWidth: 520, margin: '20px 0' }}>
                        <div style={{
                            background: '#fff', borderRadius: 16, padding: '16px 16px 4px',
                            marginBottom: 12, textAlign: 'center'
                        }}>
                            <h3 style={{ margin: '0 0 6px' }}>{t('consent:updatedTitle')}</h3>
                            <p style={{ margin: 0, fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
                                {t('consent:updatedSubtitle')}
                            </p>
                        </div>
                        <ConsentAcceptPanel onAccepted={handleAccepted} context="updated" />
                    </div>
                </div>
            )}
        </>
    );
}
